import { useEffect } from "react";
import { useStoreInfo } from "@/context/StoreInfoContext";
import Seo from "@/components/Seo";

const UPDATED = "Last updated: June 2026";

function ReachUs({ prefix = "contact us" }) {
  const { info } = useStoreInfo();
  const email = info?.email;
  const phone = info?.phone;
  return (
    <>
      {email ? (
        <>email us at <a href={`mailto:${email}`} className="text-primary hover:underline">{email}</a></>
      ) : (
        <>{prefix} through nc-mycology.com</>
      )}
      {phone ? (
        <> or call <a href={`tel:${phone.replace(/[^0-9+]/g, "")}`} className="text-primary hover:underline">{phone}</a></>
      ) : null}
    </>
  );
}

function Legal({ title, testid, children }) {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-16" data-testid={testid}>
      <Seo title={title} description={`${title} for NC Mycology LLC — lab-prepared liquid mushroom cultures, grow bags, and cultivation guides.`} path={typeof window !== "undefined" ? window.location.pathname : ""} />
      <p className="font-mono-label text-xs text-primary mb-3">Policies</p>
      <h1 className="font-display text-4xl sm:text-5xl font-light mb-2">{title}</h1>
      <p className="font-mono text-xs text-muted-foreground mb-10">{UPDATED}</p>
      <div className="space-y-6 text-[15px] leading-relaxed text-muted-foreground [&_h2]:font-display [&_h2]:text-2xl [&_h2]:text-foreground [&_h2]:mt-8 [&_h2]:mb-2 [&_strong]:text-foreground [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-2">
        {children}
      </div>
      <p className="mt-12 text-xs text-muted-foreground border-t border-border pt-6">
        This document is provided as a general template and does not constitute legal advice. Please have a licensed attorney review your policies before relying on them.
      </p>
    </div>
  );
}

export function RefundPolicy() {
  return (
    <Legal title="Refund & Replacement Policy" testid="refund-policy-page">
      <p>NC Mycology LLC stands behind the quality of every product that leaves our lab. Because we sell live biological cultures, perishable goods, and instant digital downloads, our policy is tailored to each product type below.</p>

      <h2>Live Liquid Cultures & Spawn</h2>
      <p>Liquid culture syringes and grain spawn are <strong>living, perishable biological products</strong>. We guarantee that every culture leaves our lab <strong>sterile, viable, and true to strain</strong>. We inspect and QC every batch before shipping.</p>
      <p>We will issue a <strong>free replacement or full refund</strong> if:</p>
      <ul>
        <li>The culture arrives visibly contaminated, leaking, or damaged in transit.</li>
        <li>The culture shows no viable growth despite proper storage and use, and the issue is reported within <strong>7 days of delivery</strong>.</li>
        <li>You received the wrong strain or product.</li>
      </ul>
      <p>To claim, email us within 7 days with your order number and <strong>clear photos</strong> of the syringe, the culture, and any affected grain or substrate.</p>

      <h2>Important: Contamination After Delivery</h2>
      <p>Mushroom cultivation depends on <strong>sterile technique</strong>, and once a culture leaves our lab the growing environment is outside our control. The most common reason a grow fails is contamination introduced <strong>during the customer's own inoculation, handling, or incubation</strong>, not a defect in the culture itself.</p>
      <p>For that reason, we <strong>cannot offer refunds or replacements</strong> for:</p>
      <ul>
        <li>Contamination that appears <strong>after</strong> the culture has been opened, used to inoculate, or transferred.</li>
        <li>Grows that fail due to non-sterile technique, improper temperatures, poor air exchange, or contaminated grain or substrate supplied by the customer.</li>
        <li>Cultures stored improperly (for example, frozen, overheated, or left at room temperature for extended periods).</li>
        <li>Results, yields, or growth speed, which vary widely with each grower's conditions and experience.</li>
      </ul>
      <p>We know this can be disappointing. If your grow fails, please reach out anyway. We are growers too, and we are glad to help you troubleshoot your technique so your next batch succeeds. We also publish detailed guides on sterile technique and contamination control to help you get consistent results.</p>

      <h2>Growing Kits & Physical Goods</h2>
      <p>Unopened physical goods may be returned within <strong>14 days</strong> of delivery for a refund of the product price, less shipping. Opened or used grow kits are only eligible for replacement if they arrived defective or damaged, reported within 7 days with photos.</p>

      <h2>Digital Ebooks & Guides</h2>
      <p>Because digital guides are delivered instantly and cannot be returned, <strong>all ebook and digital guide sales are final and non-refundable</strong>. If you experience a technical problem downloading your file, contact us and we will make it right.</p>

      <h2>Workshops</h2>
      <p>Workshop seats may be rescheduled or refunded up to <strong>7 days before</strong> the event date. Within 7 days, seats are non-refundable but transferable to another person.</p>

      <h2>How to Reach Us</h2>
      <p>For any refund or replacement request, please <ReachUs /> with your order number and photos where applicable. We aim to respond within 2 business days.</p>
    </Legal>
  );
}

export function ShippingPolicy() {
  return (
    <Legal title="Shipping Policy" testid="shipping-policy-page">
      <p>We ship live cultures and supplies from North Carolina to growers across the United States.</p>
      <h2>Processing Time</h2>
      <p>Orders are typically prepared and shipped within <strong>1 to 3 business days</strong>. Liquid cultures are made fresh and QC-checked before dispatch, so please allow time for preparation.</p>
      <h2>Transit & Live Cultures</h2>
      <p>Liquid cultures are hardy and travel well. In extreme heat or cold, brief temperature exposure during transit is normal and rarely affects viability. If a culture arrives visibly damaged, see our Refund & Replacement Policy.</p>
      <h2>Shipping Rates & Delivery</h2>
      <ul>
        <li>Rates are calculated at checkout based on destination and order weight.</li>
        <li>Estimated delivery is <strong>2 to 6 business days</strong> after dispatch, depending on location.</li>
        <li>You will receive tracking information by email once your order ships.</li>
      </ul>
      <h2>Digital Products</h2>
      <p>Ebooks and guides are delivered instantly and require no shipping. You can download them from your account immediately after purchase.</p>
    </Legal>
  );
}

export function PrivacyPolicy() {
  return (
    <Legal title="Privacy Policy" testid="privacy-policy-page">
      <p>NC Mycology LLC respects your privacy. This policy explains what we collect and how we use it.</p>
      <h2>Information We Collect</h2>
      <ul>
        <li><strong>Account details</strong> you provide, such as your name and email address.</li>
        <li><strong>Order information</strong>, including items purchased and shipping address.</li>
        <li><strong>Payment information</strong>, which is processed securely by our payment provider, Stripe. We never see or store your full card number.</li>
      </ul>
      <h2>How We Use It</h2>
      <ul>
        <li>To process orders, deliver products, and provide digital downloads.</li>
        <li>To respond to support requests and troubleshoot grows.</li>
        <li>To send order confirmations and, only with your consent, occasional updates.</li>
      </ul>
      <h2>Sharing</h2>
      <p>We do not sell your personal information. We share data only with service providers who help us operate the store (such as payment processing and shipping) and only as needed to fulfil your order.</p>
      <h2>Your Choices</h2>
      <p>You may request access to, correction of, or deletion of your personal data at any time by contacting us — <ReachUs />.</p>
      <h2>Cookies</h2>
      <p>We use essential cookies to keep you signed in and to remember your cart. These are required for the store to function.</p>
    </Legal>
  );
}

export function Terms() {
  return (
    <Legal title="Terms of Service" testid="terms-page">
      <p>By using the NC Mycology website and purchasing our products, you agree to these terms.</p>
      <h2>Intended Use</h2>
      <p>Our cultures and supplies are sold for the cultivation of gourmet and medicinal mushrooms. Customers are responsible for complying with all applicable local, state, and federal laws.</p>
      <h2>Product Descriptions</h2>
      <p>We work hard to describe our products accurately. Live cultures are natural products and results depend on each grower's conditions, technique, and experience. We do not guarantee specific yields or outcomes.</p>
      <h2>Health Disclaimer</h2>
      <p>Information about medicinal mushrooms is provided for educational purposes only and is not medical advice. Our products are not intended to diagnose, treat, cure, or prevent any disease. Consult a qualified professional before use.</p>
      <h2>Orders & Pricing</h2>
      <p>We reserve the right to refuse or cancel any order and to correct pricing errors. All prices are in US dollars.</p>
      <h2>Limitation of Liability</h2>
      <p>To the fullest extent permitted by law, NC Mycology LLC is not liable for any indirect or consequential damages arising from the use of our products. Our total liability is limited to the amount you paid for the product in question.</p>
      <h2>Changes</h2>
      <p>We may update these terms from time to time. Continued use of the site constitutes acceptance of the current terms.</p>
    </Legal>
  );
}
